import {
    Button,
    Checkbox,
    FormControl,
    FormControlLabel,
    FormGroup,
    FormLabel,
} from '@material-ui/core';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { styled } from '@mui/material/styles';
import * as React from 'react';
import updateTrfFile from '../actions/updateTrfFile';
import Dialog from './Dialog';

interface UpdateTRFDialogProps {
    kitId: number;
    open: boolean;
    onClose: () => void;
    onSubmit: () => void;
}

const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1,
});

const UpdateTRFDialog = ({
    kitId,
    open,
    onClose,
    onSubmit,
}: UpdateTRFDialogProps) => {
    const [file, setFile] = React.useState<File | null>(null);
    const [isTypeMatchesChecked, setIsTypeMatchesChecked] =
        React.useState(false);
    const [isAllFieldsFilledChecked, setIsAllFieldsFilledChecked] =
        React.useState(false);
    const [isLicenceStampChecked, setIsLicenceStampChecked] =
        React.useState(false);
    const error =
        !isTypeMatchesChecked ||
        !isAllFieldsFilledChecked ||
        !isLicenceStampChecked;

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const uploadedFile = event.target.files?.[0];
        if (uploadedFile) {
            if (!uploadedFile.name.endsWith('.pdf')) {
                alert('Please upload a valid PDF file');
                return;
            }
            setFile(uploadedFile);
        }
    };

    const handleClose = () => {
        setFile(null);
        setIsTypeMatchesChecked(false);
        setIsAllFieldsFilledChecked(false);
        setIsLicenceStampChecked(false);
        onClose();
    };

    const handleUploadTRF = async (e: React.SyntheticEvent) => {
        e.preventDefault();

        if (file) {
            try {
                await updateTrfFile({
                    kitId,
                    trf: file,
                });
                onSubmit();
                onClose();
            } catch (error) {
                console.error('Failed to update kit:', error);
            }
        }
        handleClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            title={'Reupload TRF'}
            onSubmit={handleUploadTRF}
            submitDisabled={error || !file}
        >
            <Button
                color="primary"
                component="label"
                role={undefined}
                variant="contained"
                tabIndex={-1}
                startIcon={<CloudUploadIcon />}
                style={{
                    marginTop: '1rem',
                    marginBottom: '1rem',
                    borderRadius: '25px',
                    boxShadow: 'none',
                    textTransform: 'none',
                    fontSize: '16px',
                }}
            >
                Upload
                <VisuallyHiddenInput
                    type="file"
                    accept=".pdf"
                    onChange={handleFileChange}
                />
            </Button>
            <p>{file?.name}</p>
            <FormControl required error={error} component="fieldset">
                <FormLabel component="legend">
                    Please confirm the following:
                </FormLabel>
                <FormGroup>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={isTypeMatchesChecked}
                                onChange={(e) =>
                                    setIsTypeMatchesChecked(e.target.checked)
                                }
                                name="typeMatches"
                            />
                        }
                        label="The TRF form matches the test type being ordered."
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={isAllFieldsFilledChecked}
                                onChange={(e) =>
                                    setIsAllFieldsFilledChecked(
                                        e.target.checked
                                    )
                                }
                                name="allFieldsFilled"
                            />
                        }
                        label="All fields of TRF are filled."
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={isLicenceStampChecked}
                                onChange={(e) =>
                                    setIsLicenceStampChecked(e.target.checked)
                                }
                                name="licenceStamp"
                            />
                        }
                        label="The TRF has a stamp containing the license number of the ordering physician."
                    />
                </FormGroup>
            </FormControl>
        </Dialog>
    );
};

export default UpdateTRFDialog;
